(function () {
  "use strict";

  const RUNTIME_KEY = "MarineMaxCoveoPageBootstrap";
  const CONFIG_SELECTOR = "[data-coveo-page-bootstrap-config]";
  const DEPENDENCY_TIMEOUT_MS = 8000;
  const DEPENDENCY_POLL_INTERVAL_MS = 30;

  const existingRuntime = window[RUNTIME_KEY];
  if (existingRuntime && typeof existingRuntime.initialize === "function") {
    existingRuntime.initialize(document);
    return;
  }

  const instances = new Map();

  function createAbortError() {
    const error = new Error("Coveo page bootstrap was aborted");
    error.name = "AbortError";
    return error;
  }

  function isAbortError(error) {
    return error && error.name === "AbortError";
  }

  function collectConfigElements(scope) {
    if (!scope) return [];

    const elements = [];
    if (typeof scope.matches === "function" && scope.matches(CONFIG_SELECTOR)) {
      elements.push(scope);
    }
    if (typeof scope.querySelectorAll === "function") {
      scope.querySelectorAll(CONFIG_SELECTOR).forEach(function (element) {
        if (!elements.includes(element)) elements.push(element);
      });
    }
    return elements;
  }

  function parseConfig(configElement) {
    try {
      const config = JSON.parse(configElement.textContent || "{}");
      const mode = String(config.mode || "").trim();
      const configUrl = String(config.configUrl || "").trim();
      if ((mode !== "search" && mode !== "collection") || !configUrl) {
        throw new Error("Invalid Coveo page bootstrap configuration");
      }
      return Object.assign({}, config, { mode, configUrl });
    } catch (error) {
      console.error("[Coveo Bootstrap] Invalid configuration:", error);
      return null;
    }
  }

  function findElementById(scope, id) {
    if (!id) return null;
    if (scope && scope !== document) {
      if (scope.id === id) return scope;
      if (typeof scope.querySelectorAll === "function") {
        const elementsWithIds = scope.querySelectorAll("[id]");
        for (let index = 0; index < elementsWithIds.length; index += 1) {
          if (elementsWithIds[index].id === id) return elementsWithIds[index];
        }
      }
    }
    return document.getElementById(id);
  }

  function findElement(scope, selector) {
    if (!selector) return null;
    if (scope && scope !== document) {
      if (typeof scope.matches === "function" && scope.matches(selector)) {
        return scope;
      }
      if (typeof scope.querySelector === "function") {
        const scopedElement = scope.querySelector(selector);
        if (scopedElement) return scopedElement;
      }
    }
    return document.querySelector(selector);
  }

  function resolveRoot(config, scope) {
    const containerId = String(config.containerId || "").trim();
    const container = findElementById(scope, containerId);
    if (container) {
      return {
        kind: config.mode === "search" ? "search" : "headless-collection",
        root: container,
      };
    }

    if (config.mode === "collection") {
      const selector =
        String(config.productListSelector || "").trim() || "#product-listing";
      const productList = findElement(scope, selector);
      if (productList) {
        return { kind: "atomic-collection", root: productList };
      }
    }

    return null;
  }

  function isInstanceActive(instance) {
    if (!instance || !instance.active) return false;
    if (instances.get(instance.root) !== instance) return false;
    if (instance.root.isConnected === false) return false;
    return !instance.abortController.signal.aborted;
  }

  function isPageHandle(value) {
    return !!value && typeof value.destroy === "function";
  }

  function destroyPageHandle(pageHandle) {
    if (!isPageHandle(pageHandle)) return false;
    try {
      pageHandle.destroy();
      return true;
    } catch (error) {
      console.error("[Coveo Bootstrap] Page teardown failed:", error);
      return false;
    }
  }

  function adoptPageHandle(instance, pageHandle) {
    if (!isPageHandle(pageHandle)) return isInstanceActive(instance);
    if (!isInstanceActive(instance)) {
      destroyPageHandle(pageHandle);
      return false;
    }
    instance.pageHandle = pageHandle;
    return true;
  }

  function destroyHeadlessPageFallback(instance) {
    if (
      !instance ||
      (instance.kind !== "search" && instance.kind !== "headless-collection")
    ) {
      return false;
    }
    const lifecycle = window.MarineMaxCoveoPageLifecycle;
    if (!lifecycle || typeof lifecycle.destroy !== "function") return false;
    try {
      lifecycle.destroy(instance.root, instance.config.mode);
      return true;
    } catch (error) {
      console.error("[Coveo Bootstrap] Page teardown failed:", error);
      return false;
    }
  }

  function waitForWindowFunction(name, signal) {
    const current = window[name];
    if (typeof current === "function") return Promise.resolve(current);

    return new Promise(function (resolve, reject) {
      const startedAt = Date.now();
      let timerId = null;

      const cleanup = function () {
        if (timerId != null) {
          window.clearTimeout(timerId);
          timerId = null;
        }
        signal.removeEventListener("abort", onAbort);
      };
      const onAbort = function () {
        cleanup();
        reject(createAbortError());
      };
      const check = function () {
        if (signal.aborted) {
          onAbort();
          return;
        }
        const dependency = window[name];
        if (typeof dependency === "function") {
          cleanup();
          resolve(dependency);
          return;
        }
        if (Date.now() - startedAt >= DEPENDENCY_TIMEOUT_MS) {
          cleanup();
          reject(new Error("Timed out waiting for window." + name));
          return;
        }
        timerId = window.setTimeout(check, DEPENDENCY_POLL_INTERVAL_MS);
      };

      signal.addEventListener("abort", onAbort, { once: true });
      check();
    });
  }

  async function fetchConfig(configUrl, signal) {
    const response = await window.fetch(configUrl, { signal });
    if (!response || !response.ok) {
      const status =
        response && response.status ? " (" + response.status + ")" : "";
      throw new Error("Failed to fetch Coveo config" + status);
    }
    return response.json();
  }

  async function bootstrapSearch(instance) {
    const signal = instance.abortController.signal;
    const dependencies = await Promise.all([
      fetchConfig(instance.config.configUrl, signal),
      waitForWindowFunction("configureHeadless", signal),
      waitForWindowFunction("initCoveoHeadlessSearchPage", signal),
    ]);
    const remoteConfig = dependencies[0];
    const configureHeadless = dependencies[1];
    const initializeSearch = dependencies[2];
    if (!isInstanceActive(instance)) throw createAbortError();

    const engine = await configureHeadless.call(window, remoteConfig);
    if (!isInstanceActive(instance)) throw createAbortError();

    instance.engine = engine;
    window.CoveoCommerceEngineForSearch = engine;
    const pageHandle = await initializeSearch.call(window, engine, remoteConfig);
    return adoptPageHandle(instance, pageHandle);
  }

  async function bootstrapHeadlessCollection(instance) {
    const signal = instance.abortController.signal;
    const dependencies = await Promise.all([
      fetchConfig(instance.config.configUrl, signal),
      waitForWindowFunction("configureHeadless", signal),
      waitForWindowFunction("initCoveoHeadlessCollectionPage", signal),
    ]);
    const remoteConfig = dependencies[0];
    const configureHeadless = dependencies[1];
    const initializeCollection = dependencies[2];
    if (!isInstanceActive(instance)) throw createAbortError();

    const engine = await configureHeadless.call(window, remoteConfig);
    if (!isInstanceActive(instance)) throw createAbortError();

    const collectionHandle = String(
      instance.root.dataset && instance.root.dataset.collectionHandle
        ? instance.root.dataset.collectionHandle
        : "",
    );
    const pageHandle = await initializeCollection.call(
      window,
      engine,
      remoteConfig,
      collectionHandle,
    );
    return adoptPageHandle(instance, pageHandle);
  }

  async function bootstrapAtomicCollection(instance) {
    const signal = instance.abortController.signal;
    const dependencies = await Promise.all([
      fetchConfig(instance.config.configUrl, signal),
      waitForWindowFunction("configureHeadless", signal),
      waitForWindowFunction("loadAtomic", signal).then(function (loadAtomic) {
        return loadAtomic.call(window);
      }),
    ]);
    const remoteConfig = dependencies[0];
    const configureHeadless = dependencies[1];
    if (!isInstanceActive(instance)) throw createAbortError();

    const engine = await configureHeadless.call(window, remoteConfig);
    if (!isInstanceActive(instance)) throw createAbortError();

    if (typeof instance.root.initializeWithEngine === "function") {
      await instance.root.initializeWithEngine(engine);
    }
    if (
      isInstanceActive(instance) &&
      typeof instance.root.executeFirstRequest === "function"
    ) {
      instance.root.executeFirstRequest();
    }
    return isInstanceActive(instance);
  }

  function disposeInstance(instance) {
    if (!instance || !instance.active) return false;
    instance.active = false;
    instance.abortController.abort();
    if (instances.get(instance.root) === instance) {
      instances.delete(instance.root);
    }
    if (
      instance.kind === "search" &&
      window.CoveoCommerceEngineForSearch === instance.engine
    ) {
      delete window.CoveoCommerceEngineForSearch;
    }
    const pageHandle = instance.pageHandle;
    instance.pageHandle = null;
    if (isPageHandle(pageHandle)) {
      destroyPageHandle(pageHandle);
    } else {
      destroyHeadlessPageFallback(instance);
    }
    return true;
  }

  function initializeConfig(configElement, scope) {
    const config = parseConfig(configElement);
    if (!config) return Promise.resolve(false);

    const resolved = resolveRoot(config, scope);
    if (!resolved) return Promise.resolve(false);

    const configKey = JSON.stringify([
      config.mode,
      config.configUrl,
      config.containerId || "",
      config.productListSelector || "",
    ]);
    const current = instances.get(resolved.root);
    if (current && current.configKey === configKey) {
      return current.promise;
    }
    if (current) disposeInstance(current);

    const instance = {
      abortController: new AbortController(),
      active: true,
      config,
      configElement,
      configKey,
      engine: null,
      kind: resolved.kind,
      pageHandle: null,
      promise: null,
      root: resolved.root,
    };

    let bootstrap = bootstrapAtomicCollection;
    if (instance.kind === "search") {
      bootstrap = bootstrapSearch;
    } else if (instance.kind === "headless-collection") {
      bootstrap = bootstrapHeadlessCollection;
    }

    instance.promise = Promise.resolve()
      .then(function () {
        return bootstrap(instance);
      })
      .catch(function (error) {
        if (!isAbortError(error)) {
          console.error("[Coveo Bootstrap] Initialization failed:", error);
        }
        disposeInstance(instance);
        return false;
      })
      .then(function (initialized) {
        if (!initialized && instances.get(instance.root) === instance) {
          instances.delete(instance.root);
          instance.active = false;
        }
        return initialized;
      });
    instances.set(instance.root, instance);
    return instance.promise;
  }

  function initialize(scope) {
    const owner = scope || document;
    return Promise.all(
      collectConfigElements(owner).map(function (configElement) {
        return initializeConfig(configElement, owner);
      }),
    );
  }

  function destroy(scope) {
    let destroyed = 0;
    instances.forEach(function (instance) {
      const belongsToScope =
        scope === instance.root ||
        scope === instance.configElement ||
        (scope &&
          typeof scope.contains === "function" &&
          (scope.contains(instance.root) ||
            scope.contains(instance.configElement)));
      if (belongsToScope && disposeInstance(instance)) destroyed += 1;
    });
    return destroyed;
  }

  const runtime = Object.freeze({
    destroy,
    has: function (root) {
      return instances.has(root);
    },
    initialize,
  });
  Object.defineProperty(window, RUNTIME_KEY, {
    configurable: false,
    enumerable: false,
    value: runtime,
    writable: false,
  });

  document.addEventListener("shopify:section:load", function (event) {
    runtime.initialize(event.target || document);
  });
  document.addEventListener("shopify:section:unload", function (event) {
    runtime.destroy(event.target || document);
  });
  document.addEventListener("turbo:load", function () {
    runtime.initialize(document);
  });
  window.addEventListener("pageshow", function () {
    runtime.initialize(document);
  });

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      function () {
        runtime.initialize(document);
      },
      { once: true },
    );
  } else {
    runtime.initialize(document);
  }
})();
